import { keyframes } from '@emotion/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { GradientSpinner, RunnerResultCard } from '@/core/components';
import type { Claim } from '@/core/types';
import { downloadFile } from '@/core/utils/download';
import { TwitterShareButton } from './TwitterShareButton';

const scaleIn = keyframes`
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const ringExpand = keyframes`
  from {
    transform: scale(0.8);
    opacity: 1;
  }
  to {
    transform: scale(2);
    opacity: 0;
  }
`;

interface ClaimStatusStepProps {
  claim: Claim;
  nftMediaUrl?: string;
}

// 処理中のステータス（動画生成中）
const PROCESSING_STATUSES = ['ready', 'video-queued', 'video-processing'] as const;

// 4秒ごとに切り替わる進捗メッセージ
const PROGRESS_MESSAGES = [
  'みきゃんがあなたの走行データを運んでいます...',
  'キツかった平田の坂のデータを読み込み中...',
  '沿道のアツい応援とハイタッチの思い出を詰め込んでいます...',
  'まもなく完成！道後温泉のように熱い動画をお届けします！',
] as const;

// 完了のステータス（動画生成完了以降 = UX的には完了）
const COMPLETE_STATUSES = ['video-done', 'mint-queued', 'mint-processing', 'mint-pending', 'completed'] as const;

// 失敗のステータス（video-failed はデータ待ち扱いのため除外）
const FAILED_STATUSES = ['mint-failed', 'permanently-failed'] as const;

export function ClaimStatusStep({ claim, nftMediaUrl }: ClaimStatusStepProps) {
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;
  const [downloading, setDownloading] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);

  const isProcessing = PROCESSING_STATUSES.includes(claim.status as (typeof PROCESSING_STATUSES)[number]);
  const isVideoProcessing = claim.status === 'video-processing';

  // video-processing になったら4秒ごとにメッセージを切り替え
  useEffect(() => {
    if (!isVideoProcessing || nftMediaUrl) return;

    const interval = setInterval(() => {
      setMessageIndex((prev) => Math.min(prev + 1, PROGRESS_MESSAGES.length - 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [isVideoProcessing, nftMediaUrl]);
  // 動画ができてれば成功（ミント成否はユーザーに関係ない）
  // fileName があれば動画生成は完了している → mint ステータスを見る必要なし
  const isComplete = COMPLETE_STATUSES.includes(claim.status as (typeof COMPLETE_STATUSES)[number]) || !!nftMediaUrl;
  // 動画がなくて失敗ステータスならエラー
  const isFailed = FAILED_STATUSES.includes(claim.status as (typeof FAILED_STATUSES)[number]) && !nftMediaUrl;
  // video-failed はデータ未受信の待機状態として扱う
  const isWaitingForData = claim.status === 'video-failed' && !nftMediaUrl;

  const handleDownload = async () => {
    if (!nftMediaUrl) return;
    setDownloading(true);
    try {
      await downloadFile(nftMediaUrl);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      {isProcessing && (
        <>
          {nftMediaUrl ? (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  background: gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                完走証ができました！
              </Typography>
              {nftMediaUrl.includes('.mp4') ? (
                <video
                  src={nftMediaUrl}
                  controls
                  autoPlay
                  muted
                  style={{
                    maxWidth: '100%',
                    width: 400,
                    borderRadius: 8,
                  }}
                />
              ) : (
                <img
                  src={nftMediaUrl}
                  alt="デジタル完走証"
                  style={{
                    maxWidth: '100%',
                    width: 400,
                    borderRadius: 8,
                  }}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <GradientSpinner size={48} />
              </Box>
              <Typography variant="h6">{isVideoProcessing ? PROGRESS_MESSAGES[messageIndex] : '準備中...'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                このページを閉じても処理は継続されます
              </Typography>
            </Box>
          )}

          <RunnerResultCard bib={claim.bib} grossTime={claim.time} netTime={claim.netTime} />
        </>
      )}

      {isComplete && (
        <>
          {/* 完了アニメーション */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
              position: 'relative',
            }}
          >
            {/* リング拡散エフェクト */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: `3px solid ${theme.palette.success.main}`,
                animation: `${ringExpand} 1s ease-out`,
                opacity: 0,
              }}
            />
            {/* チェックアイコン */}
            <CheckCircleIcon
              color="success"
              sx={{
                fontSize: 48,
                mb: 1,
                animation: `${scaleIn} 0.5s ease-out`,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              完走証ができあがりました！
            </Typography>
          </Box>

          {/* 完走証プレビュー */}
          {nftMediaUrl && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              {nftMediaUrl.includes('.mp4') ? (
                <video
                  src={nftMediaUrl}
                  controls
                  autoPlay
                  muted
                  style={{
                    maxWidth: '100%',
                    width: 400,
                    borderRadius: 8,
                  }}
                />
              ) : (
                <img
                  src={nftMediaUrl}
                  alt="デジタル完走証"
                  style={{
                    maxWidth: '100%',
                    width: 400,
                    borderRadius: 8,
                  }}
                />
              )}
            </Box>
          )}

          {/* 結果カード */}
          <Box sx={{ mb: 3 }}>
            <RunnerResultCard bib={claim.bib} grossTime={claim.time} netTime={claim.netTime} />
          </Box>

          {/* シェア・ダウンロードボタン（動画生成完了時点で表示） */}
          {nftMediaUrl && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TwitterShareButton claim={claim} />
              <Button
                variant="contained"
                fullWidth
                startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={handleDownload}
                disabled={downloading}
                sx={{
                  background: gradient,
                  color: '#fff',
                  height: 48,
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    background: gradient,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                ダウンロード
              </Button>
            </Box>
          )}

          <Button
            variant="outlined"
            fullWidth
            href={`/${claim.raceAlias}/certs`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: 'relative',
              height: 48,
              fontWeight: 600,
              border: 'none',
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              transition: 'transform 0.2s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                padding: '2px',
                background: gradient,
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                pointerEvents: 'none',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                background: theme.palette.background.paper,
              },
            }}
          >
            他の人の完走証を見る
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography
              component="a"
              href={`/${claim.raceAlias}/gallery`}
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              自分の完走証を見る →
            </Typography>
          </Box>
        </>
      )}

      {isWaitingForData && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
              p: 3,
              borderRadius: 2,
              backgroundColor: `${theme.palette.warning.light}20`,
              border: `1px solid ${theme.palette.warning.main}`,
            }}
          >
            <CircularProgress size={24} sx={{ mb: 2, color: theme.palette.warning.main }} />
            <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700, mb: 1 }}>
              データ受信待ち
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              データ受信が完了していないため、お帰りの準備をしながらお待ちください。
            </Typography>
          </Box>

          <RunnerResultCard bib={claim.bib} grossTime={claim.time} netTime={claim.netTime} />
        </>
      )}

      {isFailed && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
              p: 3,
              borderRadius: 2,
              backgroundColor: `${theme.palette.error.light}20`,
              border: `1px solid ${theme.palette.error.main}`,
            }}
          >
            <Typography variant="h6" color="error" sx={{ fontWeight: 700, mb: 1 }}>
              エラーが発生しました
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              完走証の生成中に問題が発生しました。
              <br />
              しばらく時間をおいて再度お試しいただくか、大会事務局にお問い合わせください。
            </Typography>
            {claim.lastError && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ mt: 2, fontFamily: 'monospace', wordBreak: 'break-all' }}
              >
                {claim.lastError}
              </Typography>
            )}
          </Box>

          <RunnerResultCard bib={claim.bib} grossTime={claim.time} netTime={claim.netTime} />
        </>
      )}
    </Box>
  );
}
